import { useEffect } from 'react';
import * as monaco from 'monaco-editor';

// Module-level flag to prevent re-running setup on component remount
let monacoSetupCompleted = false;

// PixiJS and game engine type definitions
const PIXI_TYPE_DEFINITIONS = `
declare module 'pixi-engine' {
  export interface Vector2 {
    x: number;
    y: number;
  }

  export interface Transform {
    position: Vector2;
    rotation: number;
    scale: Vector2;
    anchor: Vector2;
  }

  export interface GameObject {
    id: string;
    name: string;
    active: boolean;
    transform: Transform;
    parent: GameObject | null;
    children: GameObject[];
    getComponent<T extends Component>(type: new () => T): T | null;
    addComponent<T extends Component>(component: T): T;
    removeComponent<T extends Component>(type: new () => T): void;
  }

  export abstract class Component {
    gameObject: GameObject;
    enabled: boolean;

    onStart(): void;
    onUpdate(deltaTime: number): void;
    onDestroy(): void;
    onEnable(): void;
    onDisable(): void;
    onCollisionEnter(other: GameObject): void;
    onCollisionExit(other: GameObject): void;
  }

  export class SpriteRenderer extends Component {
    texture: string;
    tint: number;
    alpha: number;
    flipX: boolean;
    flipY: boolean;
    sortingLayer: number;
    sortingOrder: number;
  }

  export class Animator extends Component {
    play(name: string, loop?: boolean): void;
    stop(): void;
    pause(): void;
    resume(): void;
    setSpeed(speed: number): void;
    getCurrentAnimation(): string;
  }

  export class Collider extends Component {
    isTrigger: boolean;
    offset: Vector2;
  }

  export class BoxCollider extends Collider {
    width: number;
    height: number;
  }

  export class CircleCollider extends Collider {
    radius: number;
  }

  export class AudioSource extends Component {
    clip: string;
    volume: number;
    loop: boolean;
    playOnAwake: boolean;
    play(): void;
    stop(): void;
    pause(): void;
  }
}

declare const Input: {
  isKeyDown(key: string): boolean;
  isKeyUp(key: string): boolean;
  isKeyPressed(key: string): boolean;
  getMousePosition(): { x: number; y: number };
  isMouseButtonDown(button: number): boolean;
  isMouseButtonUp(button: number): boolean;
  isMouseButtonPressed(button: number): boolean;
  getAxis(axis: 'horizontal' | 'vertical'): number;
};

declare const Time: {
  deltaTime: number;
  time: number;
  frameCount: number;
  timeScale: number;
};

declare const Debug: {
  log(message: string): void;
  warn(message: string): void;
  error(message: string): void;
  drawLine(start: { x: number; y: number }, end: { x: number; y: number }, color?: number): void;
  drawRect(x: number, y: number, width: number, height: number, color?: number): void;
  drawCircle(x: number, y: number, radius: number, color?: number): void;
};

declare const Scene: {
  load(name: string): void;
  reload(): void;
  instantiate(prefab: string, position?: { x: number; y: number }): GameObject;
  destroy(gameObject: GameObject): void;
  find(name: string): GameObject | null;
  findAll(name: string): GameObject[];
  findWithTag(tag: string): GameObject | null;
  findAllWithTag(tag: string): GameObject[];
};

declare const Audio: {
  playOneShot(clip: string, volume?: number): void;
  playMusic(clip: string, volume?: number, loop?: boolean): void;
  stopMusic(): void;
  setMasterVolume(volume: number): void;
  setMusicVolume(volume: number): void;
  setSFXVolume(volume: number): void;
};

declare interface Math {
  lerp(a: number, b: number, t: number): number;
  clamp(value: number, min: number, max: number): number;
  random(min: number, max: number): number;
  randomInt(min: number, max: number): number;
  distance(a: { x: number; y: number }, b: { x: number; y: number }): number;
  angle(a: { x: number; y: number }, b: { x: number; y: number }): number;
  normalize(v: { x: number; y: number }): { x: number; y: number };
}

// Spine Animation API
declare interface SpineAnimatorAPI {
  play(name: string, loop?: boolean, onComplete?: () => void): void;
  stop(): void;
  pause(): void;
  resume(): void;
  queue(name: string, loop?: boolean): SpineAnimatorAPI;
  clearQueue(): void;
  timeScale: number;
  setSkin(name: string): void;
  getSkin(): string;
  getSkins(): string[];
  getCurrentAnimation(): string | null;
  getAnimations(): string[];
  setMixDuration(fromAnimation: string, toAnimation: string, duration: number): void;
  on(event: string, callback: (event: SpineEvent) => void): () => void;
  getBone(name: string): SpineBoneAPI | null;
  setBonePosition(name: string, x: number, y: number): void;
  readonly isPlaying: boolean;
  readonly currentTime: number;
}

declare interface SpineBoneAPI {
  name: string;
  x: number;
  y: number;
  rotation: number;
  scaleX: number;
  scaleY: number;
  setPosition(x: number, y: number): void;
  setRotation(degrees: number): void;
  setScale(x: number, y: number): void;
}

declare interface SpineEvent {
  name: string;
  intValue: number;
  floatValue: number;
  stringValue: string;
  audioPath?: string;
  volume?: number;
  balance?: number;
}

declare interface SpineScriptAPI {
  getAnimator(gameObjectName: string): SpineAnimatorAPI | null;
  getAnimatorById(gameObjectId: string): SpineAnimatorAPI | null;
  hasSpineRenderer(gameObjectId: string): boolean;
  getSkins(gameObjectId: string): string[];
  getAnimations(gameObjectId: string): string[];
  setSkin(gameObjectId: string, skinName: string): void;
  play(gameObjectId: string, animationName: string, loop?: boolean): void;
  stop(gameObjectId: string): void;
}

declare const Spine: SpineScriptAPI;
declare function getSpineAnimator(gameObjectName: string): SpineAnimatorAPI | null;
`;

export function useMonacoSetup() {
  useEffect(() => {
    if (monacoSetupCompleted) return;
    monacoSetupCompleted = true;

    // Configure Monaco for TypeScript
    monaco.languages.typescript.typescriptDefaults.setCompilerOptions({
      target: monaco.languages.typescript.ScriptTarget.ESNext,
      module: monaco.languages.typescript.ModuleKind.ESNext,
      moduleResolution: monaco.languages.typescript.ModuleResolutionKind.NodeJs,
      allowNonTsExtensions: true,
      allowJs: true,
      checkJs: true,
      strict: true,
      noImplicitAny: false,
      esModuleInterop: true,
      skipLibCheck: true,
      forceConsistentCasingInFileNames: true,
      jsx: monaco.languages.typescript.JsxEmit.React,
      lib: ['ESNext', 'DOM'],
    });

    // Configure Monaco for JavaScript
    monaco.languages.typescript.javascriptDefaults.setCompilerOptions({
      target: monaco.languages.typescript.ScriptTarget.ESNext,
      module: monaco.languages.typescript.ModuleKind.ESNext,
      allowNonTsExtensions: true,
      checkJs: true,
      allowJs: true,
    });

    // Set diagnostics options
    monaco.languages.typescript.typescriptDefaults.setDiagnosticsOptions({
      noSemanticValidation: false,
      noSyntaxValidation: false,
      noSuggestionDiagnostics: false,
    });

    // Add game engine type definitions
    monaco.languages.typescript.typescriptDefaults.addExtraLib(
      PIXI_TYPE_DEFINITIONS,
      'file:///node_modules/@types/pixi-engine/index.d.ts'
    );

    monaco.languages.typescript.javascriptDefaults.addExtraLib(
      PIXI_TYPE_DEFINITIONS,
      'file:///node_modules/@types/pixi-engine/index.d.ts'
    );

    // Define dark theme matching the editor
    monaco.editor.defineTheme('pixijs-dark', {
      base: 'vs-dark',
      inherit: true,
      rules: [
        { token: 'comment', foreground: '6A9955' },
        { token: 'keyword', foreground: 'C586C0' },
        { token: 'string', foreground: 'CE9178' },
        { token: 'number', foreground: 'B5CEA8' },
        { token: 'type', foreground: '4EC9B0' },
        { token: 'class', foreground: '4EC9B0' },
        { token: 'function', foreground: 'DCDCAA' },
        { token: 'variable', foreground: '9CDCFE' },
        { token: 'parameter', foreground: '9CDCFE' },
        { token: 'property', foreground: '9CDCFE' },
        { token: 'interface', foreground: '4EC9B0' },
        { token: 'namespace', foreground: '4EC9B0' },
      ],
      colors: {
        'editor.background': '#18181b', // zinc-900
        'editor.foreground': '#d4d4d8', // zinc-300
        'editor.lineHighlightBackground': '#27272a', // zinc-800
        'editor.selectionBackground': '#3f3f4680',
        'editorCursor.foreground': '#60a5fa', // blue-400
        'editorLineNumber.foreground': '#52525b', // zinc-600
        'editorLineNumber.activeForeground': '#a1a1aa', // zinc-400
        'editorIndentGuide.background': '#27272a',
        'editorIndentGuide.activeBackground': '#3f3f46',
        'editor.selectionHighlightBackground': '#3f3f4640',
        'editorBracketMatch.background': '#52525b40',
        'editorBracketMatch.border': '#52525b',
      },
    });

    // Define light theme
    monaco.editor.defineTheme('pixijs-light', {
      base: 'vs',
      inherit: true,
      rules: [],
      colors: {
        'editor.background': '#fafafa',
        'editor.foreground': '#27272a',
        'editor.lineHighlightBackground': '#f4f4f5',
        'editorLineNumber.foreground': '#a1a1aa',
      },
    });

    // Set default theme
    monaco.editor.setTheme('pixijs-dark');

    console.log('[Monaco] Themes and type definitions configured');
  }, []);
}

export function useMonacoEditor(
  editorRef: React.RefObject<monaco.editor.IStandaloneCodeEditor | null>,
  content: string,
  language: string,
  onChange?: (value: string) => void,
  onCursorChange?: (line: number, column: number) => void
) {
  useEffect(() => {
    const editor = editorRef.current;
    if (!editor) return;

    // Update content if it changed externally
    const currentValue = editor.getValue();
    if (content !== currentValue) {
      editor.setValue(content);
    }
  }, [content, editorRef]);

  useEffect(() => {
    const editor = editorRef.current;
    if (!editor || !onChange) return;

    const disposable = editor.onDidChangeModelContent(() => {
      onChange(editor.getValue());
    });

    return () => disposable.dispose();
  }, [editorRef, onChange]);

  useEffect(() => {
    const editor = editorRef.current;
    if (!editor || !onCursorChange) return;

    const disposable = editor.onDidChangeCursorPosition((e) => {
      onCursorChange(e.position.lineNumber, e.position.column);
    });

    return () => disposable.dispose();
  }, [editorRef, onCursorChange]);
}
